/**
 * Guest-booking preview dialog (DNKADM-0283, phase 1 of backlog 0022) — frames
 * the member app's public, chromeless guest funnel `/embed/book` (DNKJRX-0198)
 * so an operator can see their live catalog/branding without leaving the
 * console.
 *
 * **Target URL.** `${env.memberOrigin}/embed/book` — the member origin is
 * RECOVERED from this console's own host (`admin.<memberHost>`, see
 * `src/config/env.ts`), never configured or hardcoded. When it isn't
 * recoverable (`memberOrigin === null`) the affordance is hidden by the
 * caller; this dialog renders nothing as a defensive backstop.
 *
 * **This is the LIVE funnel**, not a sandbox: a booking submitted in the
 * preview is a real booking. The dialog labels it "Live guest experience" and
 * keeps an "Open in new tab" escape hatch always visible. A non-submitting
 * preview mode is a deferred dink-journix follow-up (spec Non-Goals).
 *
 * **Frame-blocked detection.** `/embed/book` posts `{type:'journix:ready'}`
 * to its parent on mount (the DNKJRX-0198 embed bridge, wildcard target in
 * its phase 1). A ready message whose event origin is the member origin
 * proves the frame loaded; until it arrives a connecting state shows, and if
 * it hasn't arrived within `readyTimeoutMs` the dialog swaps to a
 * frame-blocked explanation + the open-in-new-tab action instead of leaving
 * a silent blank frame. That is the expected state today: the embed route
 * still ships `X-Frame-Options: SAMEORIGIN` (cross-origin framing opens with
 * dink-journix's phase-3 `frame-ancestors` work), so the admin origin is
 * refused until that header change lands.
 *
 * **Sandbox.** The iframe runs sandboxed with the funnel's needs allowed
 * (`allow-scripts allow-same-origin allow-forms allow-popups` — the funnel
 * calls its own APIs and captures cards in a popup-capable PSP widget) and
 * top-level navigation deliberately NOT allowed (no `allow-top-navigation`):
 * the preview must never navigate the console away.
 */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { env } from '@/config/env';
import { EMBED_BOOK_PATH } from './guestBookingPreview';

/** How long to wait for the embed bridge's ready message before declaring the frame blocked. */
const DEFAULT_READY_TIMEOUT_MS = 8000;

export interface GuestBookingPreviewDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Close the dialog. */
  onClose: () => void;
  /**
   * How long to wait for the embed bridge's `journix:ready` before showing
   * the frame-blocked fallback. Test seam; defaults to 8s.
   */
  readyTimeoutMs?: number;
}

export function GuestBookingPreviewDialog(props: GuestBookingPreviewDialogProps) {
  // Remount the inner content per open so the connecting/blocked phase reseeds.
  if (!props.open) return null;
  if (env.memberOrigin === null) return null;
  return <GuestBookingPreviewDialogContent {...props} />;
}

/** The frame's lifecycle: waiting for ready → proven loaded → declared blocked. */
type FramePhase = 'connecting' | 'ready' | 'blocked';

function GuestBookingPreviewDialogContent({
  onClose,
  readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS,
}: GuestBookingPreviewDialogProps) {
  const titleId = useId();
  const [phase, setPhase] = useState<FramePhase>('connecting');
  // Non-null guarded by the wrapper above.
  const memberOrigin = env.memberOrigin as string;
  const previewUrl = `${memberOrigin}${EMBED_BOOK_PATH}`;

  // Sync with the external systems that prove or disprove the frame loaded:
  // the embed bridge's window message (accepted ONLY from the member origin)
  // and a deadline after which silence means the frame was refused.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== memberOrigin) return;
      const data: unknown = event.data;
      if (
        typeof data === 'object' &&
        data !== null &&
        (data as { type?: unknown }).type === 'journix:ready'
      ) {
        setPhase((prev) => (prev === 'connecting' ? 'ready' : prev));
      }
    }
    window.addEventListener('message', handleMessage);
    const deadline = window.setTimeout(
      () => setPhase((prev) => (prev === 'connecting' ? 'blocked' : prev)),
      readyTimeoutMs,
    );
    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(deadline);
    };
  }, [memberOrigin, readyTimeoutMs]);

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>Preview guest booking</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" role="status">
            <strong>Live guest experience</strong> — this is your real public booking
            page, exactly as guests see it. A booking submitted here is a real
            booking.{' '}
            <Link href={previewUrl} target="_blank" rel="noopener noreferrer">
              Open in new tab
            </Link>
          </Alert>

          {phase === 'blocked' ? (
            <Stack
              spacing={1.5}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 320,
                textAlign: 'center',
                px: 4,
              }}
            >
              <Icon name="visibility_off" sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography sx={{ fontWeight: 600 }}>
                The preview couldn’t be shown here
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Your booking page doesn’t allow being embedded inside the admin
                console yet. You can still see the live guest experience in its own
                tab.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="open_in_new" />}
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </Button>
            </Stack>
          ) : (
            <Box sx={{ position: 'relative', height: '65vh', minHeight: 420 }}>
              <Box
                component="iframe"
                title="Live guest experience"
                src={previewUrl}
                // The funnel needs its scripts, its own-origin API calls, its
                // forms, and PSP popups; top-level navigation stays forbidden.
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
              />
              {phase === 'connecting' && (
                <Stack
                  role="status"
                  aria-label="Connecting to your booking page"
                  spacing={1.5}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.paper',
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Connecting to your booking page…
                  </Typography>
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
